/**
 * The app's side of CompanyCam.
 *
 * Nothing here talks to CompanyCam. The token is in the Edge Function secrets
 * and the browser never sees it (CLAUDE.md rule 7), so this asks the server to
 * work the queue and reads what the server wrote down.
 *
 * There are two ways a job gets its project, and both end up in the same place:
 *
 *  - creating a lead queues a row by database trigger, and the app nudges the
 *    worker on its way out of the modal;
 *  - opening a job whose project is still missing nudges it again.
 *
 * That second one is the whole retry engine. There is no scheduler — Jonathon
 * chose that on 2026-08-22, and it is the right trade for this shop: retries
 * that only happen while somebody is using the app are enough when the app is
 * open all day, and a scheduler is one more thing to notice has quietly
 * stopped. The button is the backstop under both.
 */

import {
  COMPANYCAM_URL,
  type CompanycamLinkState,
  type Job,
} from "@roof-hawk/core";

import { supabase } from "./supabase";

/** A queued link, as the job page reads it. */
export interface CompanycamLink {
  id: string;
  job_id: string;
  state: CompanycamLinkState;
  attempts: number;
  last_error: string | null;
  next_attempt_at: string | null;
  linked_at: string | null;
  /**
   * The project's labels no longer match the job — its bid type changed, or an
   * estimate finally said what is going on the roof (ADR 0057). Raised by
   * trigger; the worker puts it right and lowers it again.
   */
  needs_label_sync: boolean;
}

/** What `link` came back with. */
export interface CompanycamOutcome {
  ok: boolean;
  project_id?: string | null;
  project_url?: string | null;
  source?: "matched" | "created";
  warnings?: string[];
  error?: string;
}

/**
 * Call the CompanyCam function, keeping the sentence it sent back.
 *
 * `functions.invoke` reports a non-2xx as a generic error and hides the body,
 * and the body is where the useful sentence is — "CompanyCam refused the API
 * token", "CompanyCam is rate-limiting us". A rep shown "non-2xx status code"
 * learns nothing at all.
 */
async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("companycam", {
    body: { ...body, app_url: window.location.origin },
  });
  if (!error) return data as T;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = await context.json().catch(() => null);
    if (payload && typeof payload.error === "string") {
      throw new Error(payload.error);
    }
  }
  throw new Error(error.message);
}

/** The queued link for a job, if there is one. */
export async function fetchCompanycamLink(
  jobId: string,
): Promise<CompanycamLink | null> {
  const { data, error } = await supabase
    .from("companycam_links")
    // One string, not a concatenation: the client infers the row type from
    // this literal, and a `+` here turns it back into a plain string.
    .select(
      "id,job_id,state,attempts,last_error,next_attempt_at,linked_at,needs_label_sync",
    )
    .eq("job_id", jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as CompanycamLink) ?? null;
}

/**
 * Ask the server to work through whatever is waiting.
 *
 * Nothing waits for this and nothing shows its result. If it fails, the rows
 * are still exactly where they were and the next person to open a job asks
 * again — which is why every caller drops the promise on purpose.
 */
export async function pushCompanycamQueue(): Promise<void> {
  try {
    await invoke({ action: "push" });
  } catch {
    // Deliberately silent. See above.
  }
}

/**
 * Link one job now — the button on the job page.
 *
 * Unlike the queue, this ignores the retry clock and revives a row that had
 * been given up on, because a person pressing a button is a better reason to
 * try than any schedule.
 */
export async function linkCompanycamJob(
  jobId: string,
): Promise<CompanycamOutcome> {
  return invoke<CompanycamOutcome>({ action: "link", job_id: jobId });
}

/**
 * Where the Photos tab goes.
 *
 * The job's own project once it has one; CompanyCam's front door until then,
 * which is what this tab did before any of this existed and is still better
 * than a dead tab.
 */
export function photosUrl(job: Pick<Job, "companycam_project_url">): string {
  return job.companycam_project_url?.trim() || COMPANYCAM_URL;
}

/** One photograph in the receipt picker. */
export interface CompanycamPhoto {
  id: string;
  captured_at: string | null;
  thumbnail: string;
}

/** A page of the job's photographs, newest first. */
export interface CompanycamPhotoPage {
  /** False when the job has no CompanyCam project yet. */
  linked: boolean;
  photos: CompanycamPhoto[];
  /** Another page is worth asking for. */
  more: boolean;
}

/**
 * The job's CompanyCam photographs, for picking a receipt out of on a phone —
 * where the CompanyCam browser extension that does this on a laptop does not
 * exist. Only that job's project: the server reads the job as the person
 * asking and refuses a photograph from anywhere else.
 */
export async function listJobPhotos(
  jobId: string,
  page = 1,
): Promise<CompanycamPhotoPage> {
  return invoke<CompanycamPhotoPage>({ action: "photos", job_id: jobId, page });
}

/**
 * One photograph at full size, as a file — the same thing the upload button
 * would have handed the receipt reader.
 */
export async function fetchJobPhoto(
  jobId: string,
  photo: Pick<CompanycamPhoto, "id">,
): Promise<File> {
  const { data, error, response } = await supabase.functions.invoke(
    "companycam",
    { body: { action: "photo", job_id: jobId, photo_id: photo.id } },
  );
  if (error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      const payload = await context.json().catch(() => null);
      if (payload && typeof payload.error === "string") {
        throw new Error(payload.error);
      }
    }
    throw new Error(error.message);
  }
  if (!(data instanceof Blob)) {
    throw new Error("CompanyCam did not send a picture back.");
  }
  const type = response?.headers.get("X-Photo-Type") || "image/jpeg";
  const extension =
    type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
  return new File([data], `companycam-${photo.id}.${extension}`, { type });
}
